import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import type { GridApi } from "ag-grid-community";
import { useViewsStore } from "../../store/viewsStore";
import type { SavedView, UnknownRecord } from "../../types/common";

// Module display names
const MODULE_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  trades: "Trades",
  curves: "Curves",
  reports: "Reports",
  settlement: "Settlement",
  "instruments-registry": "Instruments registry",
  "instruments-issuers": "Instruments issuers",
  "instruments-entities": "Instruments entities",
  "datahub-md-rate-fixings": "DataHub rate fixings",
  "datahub-md-fx-fixings": "DataHub FX fixings",
  "datahub-md-rate-curves": "DataHub rate curves",
  "datahub-settle-queue": "DataHub settlement queue",
};

interface LayoutManagerDialogProps {
  open: boolean;
  onClose: () => void;
  /** Current active module (used to capture grid state when saving) */
  activeModule?: string;
  /** Current grid API for save/load operations */
  gridApi?: GridApi | null;
}

type EditingField = { viewId: string; field: "name" | "desc"; value: string } | null;

export function LayoutManagerDialog({ open, onClose, activeModule, gridApi }: LayoutManagerDialogProps) {
  const { byId, loading, loadAll, saveView, updateView, deleteView, error } = useViewsStore();
  const [saveName, setSaveName] = useState("");
  const [saveDesc, setSaveDesc] = useState("");
  const [saveModule, setSaveModule] = useState(activeModule ?? "dashboard");
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [editing, setEditing] = useState<EditingField>(null);
  const [filterModule, setFilterModule] = useState<string>("all");
  const [opError, setOpError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) void loadAll();
  }, [open, loadAll]);

  useEffect(() => {
    if (activeModule) setSaveModule(activeModule);
  }, [activeModule]);

  useEffect(() => {
    if (editing && editInputRef.current) editInputRef.current.focus();
  }, [editing]);

  if (!open) return null;

  const allViews = Object.values(byId ?? {});
  const modules = Array.from(new Set(allViews.map((v) => v.module))).sort();
  const filtered = filterModule === "all" ? allViews : allViews.filter((v) => v.module === filterModule);
  // Group by module
  const grouped: Record<string, SavedView[]> = {};
  for (const v of filtered) {
    const m = v.module ?? "other";
    if (!grouped[m]) grouped[m] = [];
    grouped[m].push(v);
  }

  const handleSave = async () => {
    if (!saveName.trim()) return;
    setSaving(true);
    setOpError(null);
    try {
      const vc: UnknownRecord = gridApi
        ? {
            column_state: gridApi.getColumnState(),
            filter_model: gridApi.getFilterModel?.() ?? undefined,
            pivot_mode: Boolean(gridApi.isPivotMode?.()),
          }
        : {};
      await saveView(saveModule, saveName.trim(), vc, saveDesc.trim() || undefined, saveAsDefault);
      setSaveName("");
      setSaveDesc("");
      setSaveAsDefault(false);
    } catch (e) {
      setOpError(e instanceof Error ? e.message : "Failed to save layout");
    } finally {
      setSaving(false);
    }
  };

  const handleLoad = (v: SavedView) => {
    if (!gridApi) return;
    const vc = v.view_config ?? {};
    const cols = vc.column_state;
    const pivot = vc.pivot_mode;
    const filt = vc.filter_model as UnknownRecord | undefined;
    if (Array.isArray(cols)) gridApi.applyColumnState({ state: cols, applyOrder: true });
    if (typeof pivot === "boolean") gridApi.setGridOption("pivotMode", pivot);
    if (filt && typeof filt === "object") gridApi.setFilterModel(filt);
  };

  const handleSetDefault = async (v: SavedView) => {
    setOpError(null);
    try {
      await updateView(String(v.view_id), { is_default: !v.is_default });
    } catch (e) {
      setOpError(e instanceof Error ? e.message : "Failed to update");
    }
  };

  const handleUpdateConfig = async (v: SavedView) => {
    if (!gridApi) return;
    setOpError(null);
    try {
      const vc: UnknownRecord = {
        column_state: gridApi.getColumnState(),
        filter_model: gridApi.getFilterModel?.() ?? undefined,
        pivot_mode: Boolean(gridApi.isPivotMode?.()),
      };
      await updateView(String(v.view_id), { view_config: vc });
    } catch (e) {
      setOpError(e instanceof Error ? e.message : "Failed to update");
    }
  };

  const handleEditCommit = async () => {
    if (!editing) return;
    setOpError(null);
    try {
      if (editing.field === "name") {
        await updateView(editing.viewId, { view_name: editing.value });
      } else {
        await updateView(editing.viewId, { description: editing.value });
      }
    } catch (e) {
      setOpError(e instanceof Error ? e.message : "Failed to rename");
    }
    setEditing(null);
  };

  const handleDelete = async (v: SavedView) => {
    if (!confirm(`Delete layout "${v.view_name}"?`)) return;
    setOpError(null);
    try {
      await deleteView(String(v.view_id));
    } catch (e) {
      setOpError(e instanceof Error ? e.message : "Failed to delete");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="flex w-[min(100vw,760px)] flex-col rounded-lg border border-border bg-[var(--color-panel,#1c1c1c)] shadow-2xl"
        style={{ maxHeight: "80vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-neutral-100">Grid Layout Manager</h2>
          <button type="button" className="btn py-0.5 text-xs" onClick={onClose}>✕ Close</button>
        </div>

        {/* Save new layout */}
        <div className="border-b border-border px-4 py-3">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted">Save Current Grid State</div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-muted">Name</label>
              <input
                className="w-40 rounded border border-border bg-bg px-2 py-1 text-[11px]"
                placeholder="Layout name…"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") void handleSave(); }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-muted">Module</label>
              <select
                className="rounded border border-border bg-bg px-2 py-1 text-[11px]"
                value={saveModule}
                onChange={(e) => setSaveModule(e.target.value)}
              >
                {[
                  "dashboard",
                  "trades",
                  "curves",
                  "reports",
                  "settlement",
                  "instruments-registry",
                  "instruments-issuers",
                  "instruments-entities",
                  "datahub-md-rate-fixings",
                  "datahub-md-fx-fixings",
                  "datahub-md-rate-curves",
                  "datahub-settle-queue",
                ].map((m) => (
                  <option key={m} value={m}>{MODULE_LABELS[m] ?? m}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-muted">Description</label>
              <input
                className="w-44 rounded border border-border bg-bg px-2 py-1 text-[11px]"
                placeholder="Optional description…"
                value={saveDesc}
                onChange={(e) => setSaveDesc(e.target.value)}
              />
            </div>
            <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-muted pb-1">
              <input
                type="checkbox"
                checked={saveAsDefault}
                onChange={(e) => setSaveAsDefault(e.target.checked)}
              />
              Set as default
            </label>
            <button
              type="button"
              className={clsx("btn py-1 text-[11px]", (saving || !saveName.trim()) && "opacity-40")}
              disabled={saving || !saveName.trim()}
              onClick={() => void handleSave()}
            >
              {saving ? "Saving…" : "Save Layout"}
            </button>
          </div>
          {(opError || error) && (
            <p className="mt-1 text-[10px] text-bad">{opError ?? error}</p>
          )}
        </div>

        {/* Filter by module */}
        <div className="flex items-center gap-2 border-b border-border px-4 py-2">
          <span className="text-[10px] text-muted">Filter:</span>
          {["all", ...modules].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setFilterModule(m)}
              className={clsx(
                "rounded px-2 py-0.5 text-[10px] transition-colors",
                filterModule === m
                  ? "bg-accent/20 text-accent"
                  : "text-muted hover:text-neutral-200",
              )}
            >
              {m === "all" ? "All" : (MODULE_LABELS[m] ?? m)}
            </button>
          ))}
          {loading && <span className="ml-auto text-[10px] text-muted">Loading…</span>}
        </div>

        {/* Views list */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
          {Object.keys(grouped).length === 0 ? (
            <p className="py-6 text-center text-[11px] text-muted italic">No saved layouts yet.</p>
          ) : (
            Object.entries(grouped)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([mod, views]) => (
                <div key={mod} className="mb-4">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted">
                    {MODULE_LABELS[mod] ?? mod}
                  </div>
                  <div className="rounded border border-border divide-y divide-border">
                    {views
                      .sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0) || a.view_name.localeCompare(b.view_name))
                      .map((v) => (
                        <div
                          key={String(v.view_id)}
                          className="flex items-center gap-2 px-3 py-1.5 hover:bg-white/[0.03]"
                        >
                          {/* Default star */}
                          <button
                            type="button"
                            title={v.is_default ? "Default (click to unset)" : "Set as default"}
                            className={clsx(
                              "shrink-0 text-sm transition-colors",
                              v.is_default ? "text-yellow-400" : "text-border hover:text-yellow-400",
                            )}
                            onClick={() => void handleSetDefault(v)}
                          >
                            ★
                          </button>

                          {/* Name / edit */}
                          <div className="min-w-0 flex-1">
                            {editing?.viewId === String(v.view_id) && editing.field === "name" ? (
                              <input
                                ref={editInputRef}
                                className="w-full rounded border border-accent bg-bg px-1.5 py-0.5 text-[11px]"
                                value={editing.value}
                                onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") void handleEditCommit();
                                  if (e.key === "Escape") setEditing(null);
                                }}
                                onBlur={() => void handleEditCommit()}
                              />
                            ) : (
                              <span
                                className="cursor-text text-[11px] text-neutral-200 hover:text-white"
                                title="Click to rename"
                                onClick={() => setEditing({ viewId: String(v.view_id), field: "name", value: v.view_name })}
                              >
                                {v.view_name}
                              </span>
                            )}
                            {v.description && editing?.viewId !== String(v.view_id) && (
                              <span
                                className="ml-2 cursor-text text-[10px] text-muted hover:text-neutral-300"
                                onClick={() => setEditing({ viewId: String(v.view_id), field: "desc", value: v.description ?? "" })}
                              >
                                {v.description}
                              </span>
                            )}
                            {editing?.viewId === String(v.view_id) && editing.field === "desc" && (
                              <input
                                ref={editInputRef}
                                className="ml-2 w-40 rounded border border-accent bg-bg px-1.5 py-0.5 text-[10px]"
                                placeholder="description…"
                                value={editing.value}
                                onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") void handleEditCommit();
                                  if (e.key === "Escape") setEditing(null);
                                }}
                                onBlur={() => void handleEditCommit()}
                              />
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex shrink-0 items-center gap-1">
                            {v.module === activeModule && gridApi && (
                              <button
                                type="button"
                                className="btn py-0.5 text-[10px]"
                                title="Apply this layout to the current grid"
                                onClick={() => handleLoad(v)}
                              >
                                Load
                              </button>
                            )}
                            {v.module === activeModule && gridApi && (
                              <button
                                type="button"
                                className="btn py-0.5 text-[10px]"
                                title="Overwrite this layout with the current grid state"
                                onClick={() => void handleUpdateConfig(v)}
                              >
                                Update
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn py-0.5 text-[10px] text-bad/80 hover:text-bad"
                              title="Delete layout"
                              onClick={() => void handleDelete(v)}
                            >
                              Del
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
}
